import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Music, Wand2, Loader2, Play, Pause, Download, Sparkles,
  Volume2, AlertCircle, Lock, Share2, Sliders
} from 'lucide-react';
import AICreditsPanel from './AICreditsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMPT_PRESETS = [
  { label: 'Elektro Ka — Tropical', prompt: 'Elektro Ka rhythm, gwo ka percussion, deep bass, tropical vibe, Caribbean energy' },
  { label: 'House Old School', prompt: 'Classic 90s house, piano stab, soulful female vocal, four-on-the-floor kick, warm bassline' },
  { label: 'Hip Hop / Boom Bap', prompt: 'Boom bap hip hop beat, vinyl crackle, jazz sample, hard drums, bass-heavy' },
  { label: 'Reggae Roots', prompt: 'Roots reggae, organ skank, walking bass, hand percussion, conscious vibe' },
  { label: 'Afrobeat Modern', prompt: 'Modern afrobeat, log drums, mellow synth, smooth vocal harmonies, midtempo' },
  { label: 'Trap Sombre', prompt: 'Dark trap beat, 808 sub bass, fast hi-hats, eerie melody, cinematic atmosphere' },
];

const GENRES = ['Elektro Ka', 'House', 'Reggae', 'Hip Hop', 'Afrobeat', 'Zouk', 'Soul', 'Funk', 'Trap', 'Lo-Fi', 'Techno', 'Amapiano', 'Dancehall'];
const KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B', 'Cm', 'C#m', 'Dm', 'D#m', 'Em', 'Fm', 'F#m', 'Gm', 'G#m', 'Am', 'A#m', 'Bm'];

function fmtDur(s) {
  if (!s) return '--:--';
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export default function AIMusicTab() {
  const [prompt, setPrompt] = useState('');
  const [genre, setGenre] = useState('Elektro Ka');
  const [duration, setDuration] = useState(60);
  const [bpm, setBpm] = useState(120);
  const [songKey, setSongKey] = useState('Am');
  const [lyrics, setLyrics] = useState('');
  const [instrumental, setInstrumental] = useState(false);
  const [adminPwd, setAdminPwd] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [generated, setGenerated] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState('');
  const [quota, setQuota] = useState({ remaining: 1, used: 0, free_per_day: 1, is_admin: false });
  const [playingId, setPlayingId] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(() => {
    try { return localStorage.getItem('kazajah_ai_email') || ''; } catch { return ''; }
  });
  const [aiBalance, setAiBalance] = useState(null);
  const [showPacks, setShowPacks] = useState(false);
  const audioRef = useRef(null);
  const elapsedTimerRef = useRef(null);

  const fetchQuota = useCallback(async () => {
    try {
      const url = adminPwd
        ? `${API}/ai-music/quota?admin=${encodeURIComponent(adminPwd)}`
        : `${API}/ai-music/quota`;
      const r = await fetch(url);
      if (r.ok) setQuota(await r.json());
    } catch {}
  }, [adminPwd]);

  const fetchRecent = useCallback(async () => {
    try {
      const r = await fetch(`${API}/ai-music/recent?limit=10`);
      if (r.ok) setRecent(await r.json());
    } catch {}
  }, []);

  useEffect(() => { fetchQuota(); fetchRecent(); }, [fetchQuota, fetchRecent]);

  const handleGenerate = async () => {
    setError('');
    if (!prompt.trim() && !genre) {
      return setError("Donne un prompt ou un genre.");
    }
    if (duration < 10 || duration > 240) {
      return setError('Duree entre 10 et 240 secondes.');
    }
    setGenerating(true);
    setGenerated(null);
    setElapsed(0);
    elapsedTimerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    try {
      const body = {
        prompt: prompt.trim(),
        tags: genre,
        duration,
        bpm,
        key: songKey,
        instrumental,
        lyrics: lyrics.trim(),
        admin: adminPwd || undefined,
        customer_email: customerEmail || undefined,
      };
      const r = await fetch(`${API}/ai-music/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 429 || r.status === 402) {
          setError(data.detail || 'Limite atteinte');
          setShowPacks(true);
        } else {
          setError(data.detail || `Erreur ${r.status}`);
        }
        return;
      }
      setGenerated(data.track);
      setQuota(q => ({ ...q, remaining: data.remaining ?? q.remaining }));
      if (data.credit_balance) setAiBalance(data.credit_balance);
      fetchRecent();
    } catch (e) {
      setError(e.message || 'Erreur reseau');
    } finally {
      setGenerating(false);
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    }
  };

  const togglePlay = (track) => {
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      setPlayingId(track.id);
      setTimeout(() => audioRef.current?.play().catch(() => {}), 50);
    }
  };

  const playingTrack = playingId === generated?.id ? generated : recent.find(r => r.id === playingId);
  const playingSrc = playingTrack ? `${process.env.REACT_APP_BACKEND_URL}${playingTrack.audio_url}` : null;

  const remainingTxt = quota.is_admin
    ? 'Illimite (admin)'
    : `${quota.remaining}/${quota.free_per_day} gratuite${quota.free_per_day !== 1 ? 's' : ''}/jour`;

  const blocked = !quota.is_admin && quota.remaining <= 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="ai-music-tab">
      {/* Audio for playback */}
      {playingSrc && (
        <audio ref={audioRef} src={playingSrc} onEnded={() => setPlayingId(null)} className="hidden" />
      )}

      {/* Header */}
      <div className="mb-5">
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <div>
            <h2 className="text-xl sm:text-2xl font-black flex items-center gap-2">
              <Music className="text-emerald-400" size={20} /> AI Music Generator
            </h2>
            <p className="text-xs text-zinc-500 mt-1">Genere une track depuis un prompt en quelques secondes — ACE-Step.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${quota.is_admin ? 'bg-emerald-500/20 text-emerald-300' : blocked ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/15 text-yellow-300'}`}
              data-testid="ai-music-quota">{remainingTxt}</span>
            <button onClick={() => setShowAdmin(v => !v)} data-testid="ai-music-admin-toggle"
              className="px-2 py-1 text-[11px] text-zinc-500 hover:text-emerald-400 inline-flex items-center gap-1">
              <Lock size={11} /> Admin
            </button>
          </div>
        </div>
        {showAdmin && (
          <div className="flex gap-2">
            <input type="password" value={adminPwd} onChange={(e) => setAdminPwd(e.target.value)}
              onBlur={fetchQuota} placeholder="Mot de passe admin"
              className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
              data-testid="ai-music-admin-input" />
            <button onClick={fetchQuota}
              className="px-3 py-2 bg-emerald-500 text-black text-xs font-bold rounded-lg">Valider</button>
          </div>
        )}
      </div>

      {/* AI Credits panel */}
      {!quota.is_admin && (
        <div className="mb-5">
          <AICreditsPanel
            compact
            email={customerEmail}
            onEmailChange={(v) => { setCustomerEmail(v); try { localStorage.setItem('kazajah_ai_email', v); } catch {} }}
            onBalanceChange={(b) => setAiBalance(b)}
            hidePurchaseUI={!showPacks && !blocked}
          />
          {!showPacks && !blocked && aiBalance && aiBalance.balance === 0 && !aiBalance.subscription_active && (
            <button
              data-testid="ai-music-show-packs"
              onClick={() => setShowPacks(true)}
              className="mt-2 text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">
              <Sparkles size={11} /> Acheter des crédits →
            </button>
          )}
        </div>
      )}

      {/* Form */}
      <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 border border-zinc-800 rounded-2xl p-5 mb-5">
        {/* Presets */}
        <div className="flex flex-wrap gap-2 mb-4">
          {PROMPT_PRESETS.map((p) => (
            <button key={p.label} onClick={() => setPrompt(p.prompt)}
              data-testid={`ai-music-preset-${p.label.replace(/\s+/g, '-').toLowerCase()}`}
              className="px-2.5 py-1 bg-zinc-800 hover:bg-emerald-500/20 hover:text-emerald-300 text-[11px] font-medium text-zinc-400 rounded-full transition-colors">
              {p.label}
            </button>
          ))}
        </div>

        <label className="block mb-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Prompt</span>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
            placeholder="Decris ton son. Ex: 'Elektro Ka tropical avec percussion gwo ka et basse profonde'"
            className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm resize-none focus:outline-none focus:border-emerald-500"
            data-testid="ai-music-prompt-input" />
        </label>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Genre</span>
            <select value={genre} onChange={(e) => setGenre(e.target.value)}
              data-testid="ai-music-genre-select"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500">
              {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Duree (s)</span>
            <input type="number" min="10" max="240" value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 60)}
              data-testid="ai-music-duration-input"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500" />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-yellow-500 mb-1 block">BPM</span>
            <input type="number" min="40" max="240" value={bpm}
              onChange={(e) => setBpm(parseInt(e.target.value) || 120)}
              data-testid="ai-music-bpm-input"
              className="w-full px-3 py-2 bg-zinc-900 border border-yellow-700/40 rounded-lg text-yellow-400 font-bold text-sm focus:outline-none focus:border-yellow-500" />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-yellow-500 mb-1 block">Tonalite</span>
            <select value={songKey} onChange={(e) => setSongKey(e.target.value)}
              data-testid="ai-music-key-select"
              className="w-full px-3 py-2 bg-zinc-900 border border-yellow-700/40 rounded-lg text-yellow-400 font-bold text-sm focus:outline-none focus:border-yellow-500">
              {KEYS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={instrumental} onChange={(e) => setInstrumental(e.target.checked)}
              data-testid="ai-music-instrumental-toggle"
              className="w-4 h-4 accent-emerald-500" />
            <span className="text-sm text-zinc-300">Instrumental</span>
          </label>
          <span className="text-[10px] text-zinc-600 ml-auto">
            Le BPM et la tonalite seront pris en compte par l'IA et conserves dans la timeline DAW
          </span>
        </div>

        {!instrumental && (
          <label className="block mb-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Paroles (optionnel)</span>
            <textarea value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={3}
              placeholder="[verse]&#10;Premier couplet ici&#10;[chorus]&#10;Refrain ici"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm font-mono resize-none focus:outline-none focus:border-emerald-500"
              data-testid="ai-music-lyrics-input" />
          </label>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 mb-3 bg-red-500/10 border border-red-500/30 rounded-xl"
            data-testid="ai-music-error">
            <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        <button onClick={handleGenerate} disabled={generating || blocked}
          data-testid="ai-music-generate-button"
          className="w-full py-3.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-black font-black rounded-2xl hover:scale-[1.01] active:scale-[0.99] transition-transform disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20">
          {generating ? (
            <><Loader2 size={18} className="animate-spin" /> Generation... {elapsed}s / ~{Math.max(20, Math.round(duration * 1.5))}s</>
          ) : blocked ? (
            <><Lock size={16} /> Limite atteinte — reviens demain</>
          ) : (
            <><Sparkles size={16} /> Generer la musique</>
          )}
        </button>
        {generating && (
          <div className="mt-2 h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all duration-1000"
              style={{ width: `${Math.min(95, (elapsed / Math.max(20, Math.round(duration * 1.5))) * 100)}%` }} />
          </div>
        )}
        {!quota.is_admin && (
          <p className="text-[10px] text-zinc-600 text-center mt-2">
            1 generation gratuite par jour. La generation prend environ 30 secondes.
          </p>
        )}
      </div>

      {/* Just-generated result */}
      {generated && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-900/20 via-zinc-900 to-zinc-950 border border-emerald-500/30 rounded-2xl p-4 mb-5"
          data-testid="ai-music-result">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">Nouveau track</p>
              <h3 className="text-white font-bold text-base truncate">{generated.prompt || generated.tags || 'AI Music'}</h3>
              <p className="text-zinc-400 text-xs truncate">
                {generated.tags}
                {generated.bpm ? ` • ${generated.bpm} BPM` : ''}
                {generated.key ? ` • ${generated.key}` : ''}
                {' • '}{fmtDur(generated.duration)}
              </p>
            </div>
            <button onClick={() => togglePlay(generated)}
              data-testid="ai-music-result-play"
              className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 text-black flex items-center justify-center shadow-lg flex-shrink-0">
              {playingId === generated.id ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => {
              const detail = {
                url: `${process.env.REACT_APP_BACKEND_URL}${generated.audio_url}`,
                name: generated.prompt?.slice(0, 40) || generated.tags || 'AI Music',
                bpm: generated.bpm,
                key: generated.key,
              };
              window.dispatchEvent(new CustomEvent('kazajah:send-to-daw', { detail }));
            }}
              data-testid="ai-music-send-to-daw"
              className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-bold rounded-full inline-flex items-center gap-1.5 shadow-lg shadow-yellow-500/20">
              <Sliders size={12} /> Envoyer au DAW
            </button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}${generated.audio_url}`} download={`kazajah-ai-${generated.id}.wav`}
              data-testid="ai-music-result-download"
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold rounded-full inline-flex items-center gap-1.5">
              <Download size={12} /> Telecharger
            </a>
            <button onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: `KAZAJAH AI Music`,
                  text: generated.prompt || generated.tags,
                  url: `${process.env.REACT_APP_BACKEND_URL}${generated.audio_url}`
                }).catch(() => {});
              } else {
                navigator.clipboard?.writeText(`${process.env.REACT_APP_BACKEND_URL}${generated.audio_url}`);
              }
            }}
              data-testid="ai-music-result-share"
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold rounded-full inline-flex items-center gap-1.5">
              <Share2 size={12} /> Partager
            </button>
            <span className="text-[11px] text-zinc-600 ml-auto">Genere le {new Date(generated.created_at).toLocaleString('fr-FR')}</span>
          </div>
        </motion.div>
      )}

      {/* Recent generations */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
            <Wand2 size={14} className="text-emerald-400" /> Generations recentes
          </h3>
          <span className="text-[11px] text-zinc-600">{recent.length} pieces</span>
        </div>
        {recent.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-zinc-800 rounded-2xl">
            <Music size={24} className="text-zinc-700 mx-auto mb-2" />
            <p className="text-zinc-500 text-xs">Aucune generation pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {recent.map((t) => (
              <div key={t.id}
                data-testid={`ai-music-recent-${t.id}`}
                className="flex items-center gap-2.5 p-2.5 bg-zinc-900 hover:bg-zinc-800/80 border border-zinc-800 rounded-xl transition-colors">
                <button onClick={() => togglePlay(t)}
                  className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${playingId === t.id ? 'bg-emerald-500 text-black' : 'bg-zinc-800 text-emerald-400'}`}>
                  {playingId === t.id ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold truncate">{t.prompt || t.tags || 'AI Music'}</p>
                  <p className="text-[10px] text-zinc-500 truncate">
                    {t.tags || 'Sans genre'}
                    {t.bpm ? ` • ${t.bpm} BPM` : ''}
                    {t.key ? ` • ${t.key}` : ''}
                    {' • '}{fmtDur(t.duration)}
                    {' • '}{new Date(t.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <button onClick={() => {
                  window.dispatchEvent(new CustomEvent('kazajah:send-to-daw', {
                    detail: {
                      url: `${process.env.REACT_APP_BACKEND_URL}${t.audio_url}`,
                      name: (t.prompt || t.tags || 'AI Music').slice(0, 40),
                      bpm: t.bpm,
                      key: t.key,
                    }
                  }));
                }}
                  data-testid={`ai-music-recent-to-daw-${t.id}`}
                  className="p-1.5 text-yellow-500 hover:text-yellow-400 transition-colors flex-shrink-0"
                  title="Envoyer au DAW">
                  <Sliders size={14} />
                </button>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${t.audio_url}`} download
                  className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors flex-shrink-0">
                  <Download size={14} />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
